/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useEffect, useMemo, useRef } from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import { breakpoints } from '@coursera/cds-core';
import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';

import { CoachChat } from 'bundles/ai-coach-platform/components/patterns';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import CourseraAiFooter from 'bundles/unified-common/components/coach-on-discovery/CourseraAiFooter';
import CourseraAiHeader from 'bundles/unified-common/components/coach-on-discovery/CourseraAiHeader';
import CourseraAiMessage from 'bundles/unified-common/components/coach-on-discovery/CourseraAiMessage';
import CourseraAiMessageLoader from 'bundles/unified-common/components/coach-on-discovery/CourseraAiMessageLoader';
import CourseraAiTemporaryStreamedMessage from 'bundles/unified-common/components/coach-on-discovery/CourseraAiTemporaryStreamedMessage';
import { WES_CHATBOT_DIALOG_ID, getActiveCoachModeId } from 'bundles/wes-common/components/WESAskCoursera/constants';
import { useIsWesCoachBrandUpdateEnabled } from 'bundles/wes-common/utils/featureFlags';

import _t from 'i18n!nls/wes-common';

import WESAskCourseraEmptyState from './WESAskCourseraEmptyState';
import type { QuickPrompt } from './WESAskCourseraEmptyState';

type Position = 'bottom-right' | 'bottom-left';

type WESAskCourseraPopupProps = {
  onClose: () => void;
  contextConfig: Record<string, unknown>;
  position?: Position;
  quickPrompts?: QuickPrompt[];
  welcomeMessage?: string;
  initialMessage?: string | null;
  onInitialMessageSent?: () => void;
};

const styles = {
  popupContainer: (position: Position) => css`
    display: flex;
    flex-direction: column;
    position: fixed;
    ${position === 'bottom-right' ? 'right: var(--cds-spacing-800);' : 'left: var(--cds-spacing-400);'}
    bottom: var(--cds-spacing-600);
    width: 400px;
    max-width: calc(100vw - 20px);
    height: 600px;
    border: 2px solid var(--cds-color-grey-200);
    border-radius: var(--cds-border-radius-200);
    box-shadow: var(--cds-elevation-level2);
    background-color: var(--cds-color-white-0);
    z-index: 101;
    overflow: hidden;
    transition: width 0.5s, height 0.5s;
    animation: var(--labs-animation-slide-in-up);

    ${breakpoints.down('sm')} {
      width: 100vw;
      height: 100vh;
      max-width: 100vw;
      max-height: 100vh;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 0;
    }
  `,

  coachChatStyles: css`
    padding: 0;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    /*
     * Override the min-height from CoachChat's lastMessage style which uses 100vh.
     * In a fixed 600px popup, we need to calculate based on the popup height, not viewport.
     * Formula: popup height (600px) - header (~80px) - footer (~120px) - padding (~40px) = ~360px.
     * Using a more conservative value to prevent blank spaces while maintaining scroll behavior.
     */
    .coach-message-response:last-of-type,
    .coach-message-streamed,
    .coach-message-loader {
      min-height: 150px !important;
    }

    ${breakpoints.down('sm')} {
      /* On mobile where popup is fullscreen, revert to viewport-based calculation */
      .coach-message-response:last-of-type,
      .coach-message-streamed,
      .coach-message-loader {
        min-height: calc(100vh - 320px) !important;
      }
    }
  `,

  headerTitle: css`
    display: flex;
    align-items: center;
    font-weight: 600;
    color: var(--cds-color-neutral-background-primary);
  `,

  headerButtons: css`
    display: flex;
    gap: 4px;
  `,
};

const WESAskCourseraPopup = ({
  onClose,
  contextConfig,
  position = 'bottom-right',
  quickPrompts,
  welcomeMessage,
  initialMessage,
  onInitialMessageSent,
}: WESAskCourseraPopupProps) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const isBrandUpdateEnabled = useIsWesCoachBrandUpdateEnabled();
  const activeCoachModeId = getActiveCoachModeId();
  const sendMessageRef = useRef<((message: string) => void) | null>(null);
  const hasSentInitialMessage = useRef(false);

  // Capture sendMessage from CoachChat's internal implementation via renderCustomInputField
  // This ensures we use CoachChat's logic (intention routing, error handling, callbacks, etc.)
  // rather than bypassing it by calling the dispatcher directly
  const renderCustomInputField = useCallback<
    (args: { sendMessage: (message: string, action: string) => void }) => null
  >(({ sendMessage }: { sendMessage: (message: string, action: string) => void }) => {
    const wrappedSendMessage = (message: string) => sendMessage(message, 'TYPED_MESSAGE');
    sendMessageRef.current = wrappedSendMessage;
    return null; // Return null to use default input field
  }, []);

  // Poll for sendMessage readiness
  // UPDATE_CHAT_HISTORY replaces the message list, so sending before it resolves
  // causes the user's question bubble to disappear. We retry every 2000ms until
  // sendMessageRef is available, with a 6s safety timeout.
  useEffect(() => {
    if (!initialMessage || hasSentInitialMessage.current) return undefined;

    const POLL_INTERVAL_MS = 2000; // Check every 2s if sendMessage is ready
    const MAX_WAIT_MS = 6000; // Stop polling after 6s to prevent infinite retries
    let elapsed = 0;

    const interval = setInterval(() => {
      elapsed += POLL_INTERVAL_MS;
      if (sendMessageRef.current) {
        clearInterval(interval);
        hasSentInitialMessage.current = true;
        sendMessageRef.current(initialMessage);
        onInitialMessageSent?.();
      } else if (elapsed >= MAX_WAIT_MS) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [initialMessage, onInitialMessageSent]);

  const handleSendMessage = useCallback<(message: string) => void>((message: string) => {
    if (sendMessageRef.current) {
      sendMessageRef.current(message);
    }
  }, []);

  const emptyState = useMemo(
    () => (
      <WESAskCourseraEmptyState
        sendMessage={handleSendMessage}
        quickPrompts={quickPrompts}
        welcomeMessage={welcomeMessage}
      />
    ),
    [handleSendMessage, quickPrompts, welcomeMessage]
  );

  return (
    <div
      id={WES_CHATBOT_DIALOG_ID}
      role="dialog"
      aria-label={isBrandUpdateEnabled ? _t('Coursera AI Chat') : _t('Ask Coursera')}
      css={styles.popupContainer(position)}
      ref={popupRef}
      data-testid="WESAskCourseraPopup"
    >
      <ErrorBoundaryWithTagging componentName="WESAskCoursera">
        <CoachChat
          key="wes-coach-chat-instance"
          mode={activeCoachModeId}
          chatSessionKey="wes-checkout-session" // Shared key across all WES bundles (teams-v2, ent-website) for unified chat history
          storeSessionId={true} //  Explicitly store session ID in session storage to persist chat session
          showSettings={false} // with custom header, we don't need to show the settings button, as with true it was casuing some UI issues
          enableRichInputField={true}
          contextConfig={contextConfig}
          trackingData={{
            mode: activeCoachModeId,
            location: 'chat',
          }}
          fetchChatHistoryOnLocationChange={false}
          renderCustomInputField={renderCustomInputField}
          emptyState={emptyState}
          customHeader={
            <CourseraAiHeader
              icon={isBrandUpdateEnabled ? <AIGenerateBrandedNewIcon size="medium" /> : undefined}
              hideTitle={isBrandUpdateEnabled}
            />
          }
          renderCustomMessage={(props) => <CourseraAiMessage {...props} />}
          renderCustomMessageLoader={(props) => <CourseraAiMessageLoader {...props} />}
          renderCustomTemporaryStreamedMessage={(props) => <CourseraAiTemporaryStreamedMessage {...props} />}
          customFooter={<CourseraAiFooter />}
          enableIntentionRouting={true}
          enableTextStreaming={true}
          enableFindInChat={true}
          enableContinuousChat={false}
          onClose={onClose}
          additionalStyles={styles.coachChatStyles}
          data-testid="WESAskCourseraChatContainer"
          enableBrandUpdate={isBrandUpdateEnabled}
          enableLoggedOutMode={!isAuthenticatedUser()}
          activeChatSessionId="wes-checkout-session"
        />
      </ErrorBoundaryWithTagging>
    </div>
  );
};

export default WESAskCourseraPopup;
